import React, { useEffect, useRef } from 'react';

/**
 * Lava-lamp goo bubbles. Mirrors the shadcn.io "Bubble Background"
 * component: several color orbs drift/orbit behind the content, an
 * SVG goo filter melts overlapping orbs into blobs, and one
 * cursor-following orb trails the pointer with springy easing.
 *
 * Kept dependency-free (no framer-motion spring) so we can render
 * it under the quiz without coupling the PWA bundle to anything
 * shadcn-specific.
 */
export default function BubbleBackground({
  className = '',
  interactive = true,
  // Near-monochrome violet palette — all shades of the same family
  // so the bubbles read as one soft gradient, not a rainbow.
  colors = {
    first: '167, 139, 250',   // violet-400
    second: '139, 92, 246',   // violet-500
    third: '196, 181, 253',   // violet-300
    fourth: '124, 58, 237',   // violet-600
    fifth: '221, 214, 254',   // violet-200
    cursor: '167, 139, 250'
  },
  blending = 'soft-light'
}) {
  const cursorRef = useRef(null);
  const rafRef = useRef(0);
  const targetRef = useRef({ x: 0, y: 0 });
  const posRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!interactive) return undefined;
    const el = cursorRef.current;
    if (!el) return undefined;
    const parent = el.parentElement;
    if (!parent) return undefined;

    const onMove = (e) => {
      const rect = parent.getBoundingClientRect();
      const point = e.touches ? e.touches[0] : e;
      targetRef.current.x = point.clientX - rect.left;
      targetRef.current.y = point.clientY - rect.top;
    };

    const tick = () => {
      const t = targetRef.current;
      const p = posRef.current;
      // Springy lerp
      p.x += (t.x - p.x) * 0.08;
      p.y += (t.y - p.y) * 0.08;
      el.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) translate(-50%, -50%)`;
      rafRef.current = requestAnimationFrame(tick);
    };

    parent.addEventListener('pointermove', onMove);
    parent.addEventListener('touchmove', onMove, { passive: true });
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      parent.removeEventListener('pointermove', onMove);
      parent.removeEventListener('touchmove', onMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, [interactive]);

  const c = colors;
  return (
    <div
      className={`absolute inset-0 overflow-hidden ${className}`}
      style={{
        // Soft, almost-monochrome violet base (violet-100 → violet-200)
        background: 'linear-gradient(160deg, #ede9fe 0%, #ddd6fe 60%, #c4b5fd 100%)'
      }}
      aria-hidden="true"
    >
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <filter id="bubble-goo">
            <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8"
              result="goo"
            />
            <feBlend in="SourceGraphic" in2="goo" />
          </filter>
        </defs>
      </svg>

      <style>{`
        @keyframes bubble-v { 0%{transform:translateY(-50%)} 50%{transform:translateY(50%)} 100%{transform:translateY(-50%)} }
        @keyframes bubble-h { 0%{transform:translateX(-50%)} 50%{transform:translateX(50%)} 100%{transform:translateX(-50%)} }
        @keyframes bubble-orbit {
          0%   { transform: rotate(0deg) translate(8%, 0) rotate(0deg); }
          100% { transform: rotate(360deg) translate(8%, 0) rotate(-360deg); }
        }
        @keyframes bubble-orbit-rev {
          0%   { transform: rotate(0deg) translate(-10%, 0) rotate(0deg); }
          100% { transform: rotate(-360deg) translate(-10%, 0) rotate(360deg); }
        }
      `}</style>

      <div
        className="absolute inset-0"
        style={{ filter: 'url(#bubble-goo) blur(40px)' }}
      >
        <div
          style={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            top: '10%',
            left: '10%',
            background: `radial-gradient(circle at center, rgba(${c.first},0.8) 0, rgba(${c.first},0) 50%) no-repeat`,
            mixBlendMode: blending,
            animation: 'bubble-v 30s ease-in-out infinite',
            transformOrigin: 'center center'
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            top: '10%',
            left: '10%',
            background: `radial-gradient(circle at center, rgba(${c.second},0.8) 0, rgba(${c.second},0) 50%) no-repeat`,
            mixBlendMode: blending,
            animation: 'bubble-orbit-rev 20s linear infinite',
            transformOrigin: 'calc(50% - 400px)'
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            top: '10%',
            left: '10%',
            background: `radial-gradient(circle at center, rgba(${c.third},0.8) 0, rgba(${c.third},0) 50%) no-repeat`,
            mixBlendMode: blending,
            animation: 'bubble-orbit 40s linear infinite',
            transformOrigin: 'calc(50% + 400px)'
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            top: '10%',
            left: '10%',
            background: `radial-gradient(circle at center, rgba(${c.fourth},0.8) 0, rgba(${c.fourth},0) 50%) no-repeat`,
            mixBlendMode: blending,
            opacity: 0.7,
            animation: 'bubble-h 40s ease-in-out infinite',
            transformOrigin: 'center center'
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: '160%',
            height: '160%',
            top: '-30%',
            left: '-30%',
            background: `radial-gradient(circle at center, rgba(${c.fifth},0.8) 0, rgba(${c.fifth},0) 50%) no-repeat`,
            mixBlendMode: blending,
            animation: 'bubble-orbit 20s linear infinite',
            transformOrigin: 'calc(50% - 800px) calc(50% + 200px)'
          }}
        />
        {interactive && (
          <div
            ref={cursorRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `radial-gradient(circle at center, rgba(${c.cursor},0.8) 0, rgba(${c.cursor},0) 50%) no-repeat`,
              mixBlendMode: blending,
              opacity: 0.7,
              pointerEvents: 'none',
              willChange: 'transform'
            }}
          />
        )}
      </div>
    </div>
  );
}
